<template lang="pug">
.editable-text.flex(
  ref="containerRef",
  :class="multiline ? 'items-start' : 'items-center'"
)
  template(v-if="isEditing")
    .max-w-md
      el-input.mr-5(
        v-model="input",
        v-bind="inputSettings",
        @keyup.enter.exact="save",
        :type="multiline ? 'textarea' : 'text'",
        :rows="1",
        :autosize="multiline ? { minRows: 1 } : false"
      )
    el-button.et-save.ml-2(
      v-loading="loading",
      type="primary",
      size="small",
      :disabled="saveDisabled",
      icon="Check",
      @click="save",
      :aria-label="$t('save')",
      :link="isCollapsed"
    ) {{ isCollapsed ? undefined : $t("save") }}
    el-button.et-cancel(
      size="small",
      @click="cancel",
      icon="Close",
      :aria-label="$t('cancel')",
      :link="isCollapsed"
    ) {{ isCollapsed ? undefined : $t("cancel") }}
  template(v-else)
    slot
    el-button.ml-5(v-if="!disabled", circle, text, @click="isEditing = true")
      el-icon
        EditPen
</template>

<script setup lang="ts">
import { showResponseErrorToUser } from "@shared/util/genericResponseErrors";
import { ElNotification } from "element-plus";
import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["cancel", "save", "onSaveCompleted"]);

const props = defineProps<{
  content?: string;
  saveCallback?: (value: string) => void;
  inputSettings?: object;
  // If set only show check and cross for save/cancel
  small?: boolean;
  multiline?: boolean;

  disabled?: boolean;
  // don't allow empty
  disableEmpty?: boolean;
}>();

const input = ref("");

const isEditing = ref(false);

const containerRef = ref<HTMLElement | null>(null);
const containerWidth = ref(Infinity);
let resizeObserver: ResizeObserver | null = null;

const isCollapsed = computed(() => props.small || containerWidth.value < 500);

const { t } = useI18n();

const saveDisabled = computed(() => {
  if (props.disableEmpty && input.value === "") {
    return true;
  }
  return false;
});

onMounted(() => {
  input.value = props.content ?? "";
  if (containerRef.value) {
    resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) containerWidth.value = entries[0].contentRect.width;
    });
    resizeObserver.observe(containerRef.value);
  }
});

onUnmounted(() => {
  resizeObserver?.disconnect();
});

watch(
  () => props.content,
  () => {
    input.value = props.content ?? "";
  },
);

const loading = ref(false);

function save() {
  if (loading.value) {
    return;
  }
  emit("save", input.value);
  if (props.saveCallback) {
    loading.value = true;
    props
      .saveCallback(input.value)
      .then(
        () => {
          emit("onSaveCompleted");
          isEditing.value = false;
        },
        (error) => {
          showResponseErrorToUser(error, t);
        },
      )
      .finally(() => {
        loading.value = false;
      });
  }
}

function cancel() {
  isEditing.value = false;
  emit("cancel");
}
</script>
